import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { formatLevelDisplay, getLevelIcon } from '../../utils/levelUtils';
import FormatCard from './share/ShareCardFormats';

const Card = styled.div`
  background: linear-gradient(135deg, #FFFFFF 0%, #F8F8F8 100%);
  border-radius: 16px;
  padding: 24px;
  width: 600px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border: 2px solid ${theme.colors.brandYellow};
  font-family: ${theme.typography.bodyFont};
  position: relative;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.softGray};
`;

const UserName = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 14px;
  font-weight: normal;
  color: #808080;
  text-align: right;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
`;

const LogoText = styled.div`
  color: ${theme.colors.deepNavy};
  font-family: ${theme.typography.headingFont};
  font-size: 28px;
  font-weight: bold;
`;

const ActivityTitle = styled.h2`
  font-family: ${theme.typography.headingFont};
  font-size: 28px;
  font-weight: bold;
  color: ${theme.colors.deepNavy};
  margin: 0 0 16px 0;
  line-height: 1.3;
`;

const ImageContainer = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${theme.colors.softGray};
  background-color: ${theme.colors.white};
  max-height: 480px;
`;

const ActivityImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 480px;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const PlaceholderImageContainer = styled(ImageContainer)`
  height: 300px;
  background-color: ${theme.colors.softGray};
`;

const CauseSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const CauseBadge = styled.div`
  background-color: ${theme.colors.brandYellow};
  color: ${theme.colors.deepNavy};
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const LevelBadge = styled.div`
  background-color: ${theme.colors.deepNavy};
  color: ${theme.colors.white};
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const BadgeIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const Reflection = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: 16px;
  color: ${theme.colors.deepNavy};
  line-height: 1.6;
  margin: 0 0 20px 0;
  font-style: italic;
  padding: 16px;
  background-color: rgba(255, 209, 0, 0.1);
  border-radius: 8px;
  border-left: 4px solid ${theme.colors.brandYellow};
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 2px solid ${theme.colors.softGray};
`;

const AppURL = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 14px;
  color: ${theme.colors.slateGray};
  font-weight: 600;
`;

const DateText = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 14px;
  color: ${theme.colors.slateGray};
`;

/**
 * ShareCard Component
 * This component is rendered to an image for social sharing
 * It displays a branded card with the user's activity details.
 * format: 'classic' (original 600px card) | 'story' | 'feed' | 'link'
 */
const ShareCard = React.forwardRef(({ activity, userName, format = 'classic', photo }, ref) => {
  // Release 14: platform-shaped formats (story / feed / link)
  if (format !== 'classic') {
    return <FormatCard ref={ref} format={format} activity={activity} userName={userName} photo={photo} />;
  }

  const formatDate = (timestamp) => {
    if (timestamp && timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    }
    return new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Card ref={ref}>
      <Header>
        <LogoContainer>
          <LogoImage src="/logo192.png" alt="Lightway" />
          <LogoText>Lightway</LogoText>
        </LogoContainer>
        {userName && <UserName>{userName}</UserName>}
      </Header>

      <ActivityTitle>{activity.title}</ActivityTitle>

      {(activity.imageUrl || activity.thumbnailUrl) && (
        <ImageContainer>
          <ActivityImage
            src={activity.imageUrl || activity.thumbnailUrl}
            alt="Activity"
          />
        </ImageContainer>
      )}

      {!activity.imageUrl && !activity.thumbnailUrl && (
        <PlaceholderImageContainer>
          <img
            src={activity.cause?.icon || '/icons/icon_activity.png'}
            alt={activity.cause?.title || 'Activity'}
            style={{ width: '120px', height: '120px', objectFit: 'contain', opacity: 0.6 }}
          />
        </PlaceholderImageContainer>
      )}

      <CauseSection>
        <CauseBadge>
          {activity.cause.icon && <BadgeIcon src={activity.cause.icon} alt="" />}
          {activity.cause.title}
        </CauseBadge>
        {activity.cause.level && (
          <LevelBadge>
            {getLevelIcon(activity.cause.level) && (
              <BadgeIcon src={getLevelIcon(activity.cause.level)} alt="" />
            )}
            {formatLevelDisplay(activity.cause.level)}
          </LevelBadge>
        )}
      </CauseSection>

      {activity.reflection && (
        <Reflection>
          "{activity.reflection}"
        </Reflection>
      )}

      <Footer>
        <AppURL>lightway-app.lightandcover.org</AppURL>
        <DateText>{formatDate(activity.timestamp)}</DateText>
      </Footer>
    </Card>
  );
});

ShareCard.displayName = 'ShareCard';

export default ShareCard;
